import { newIdentifier } from '@cadet-dsl';
import type { Doc } from '@doc';
import type { Point } from '@geom';
import { DISTANCE_EPSILON } from '@geom';
import { Elements, isForest, type NID } from '@graph';
import { localeManager } from '@reactive';
import { StringId } from '@strings';
import { showNotification, SlVariant } from '@ui';
import { expectDefined, isClose } from '@utils';
import type { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export function arrangeDryRun(
  startPosition: Point,
  endPosition: Point,
  endElement: Elements,
  context: CommandContext
): boolean {
  try {
    return arrange(startPosition, endPosition, endElement, context, true) !== undefined;
  } catch {
    return false;
  }
}

export interface ArrangeResult {
  doc: Doc;
  pan: Point;
  zoom: number;
}

export function arrange(
  startPosition: Point,
  endPosition: Point,
  endElement: Elements,
  context: CommandContext,
  dryRun = false
): ArrangeResult | undefined {
  let { doc } = context;
  const { layouter, selection } = context;

  const oldDoc = doc;

  try {
    // Calculate the delta between the start and end positions.
    const delta = endPosition.sub(startPosition);

    // Trivially reject if the start and end positions are the same.
    if (isClose(delta.magnitude, DISTANCE_EPSILON)) {
      return;
    }

    // Collect a Set of all nodes in the selection, and all the source and
    // target nodes of the edges in the selection.
    const nodes = new Set(selection.nodes);
    for (const edge of selection.edges) {
      nodes.add(doc.graph.source(edge));
      nodes.add(doc.graph.target(edge));
    }
    // The nodes to move are the selection nodes and all their descendants in
    // the group hierarchy.
    const descendants = doc.graph.forest.transitiveSuccessors(nodes);
    const nodesToMove = nodes.union(descendants);

    // Determine the new parent group for the nodes to move.
    let newParent: NID | undefined;
    if (!endElement.isEmpty) {
      // If the end element is a group, use it as the new parent.
      const endNode = endElement.anyNode;
      if (endNode !== undefined) {
        if (doc.isGroup(endNode)) {
          newParent = endNode;
        } else {
          // If the end element is a node, use the parent of the node as the new
          // parent.
          const parent = doc.graph.parent(endNode);
          if (parent !== undefined) {
            newParent = parent;
          }
        }
      } else {
        // If the end element is an edge, use the least common ancestor of the
        // source and target nodes of the edge as the new parent.
        const edge = endElement.anyEdge;
        if (edge !== undefined) {
          const [source, target] = doc.graph.endpoints(edge);
          newParent = doc.graph.leastCommonAncestor(new Set([source, target]));
        }
      }
    }

    // Find the topmost nodes that need to be reparented in the graph hierarchy.
    // For each node to move:
    // - Skip if it's already under the correct parent
    // - Find the path from root to the node
    // - If the node is part of a selected group, reparent the topmost selected ancestor
    // - Otherwise, reparent the node itself directly
    // This ensures we maintain proper group hierarchies during reparenting operations.
    const nodesToReparent = new Set<NID>();
    for (const node of nodesToMove) {
      const parent = doc.graph.parent(node);
      if (parent === newParent) {
        continue;
      }
      const pathFromRoot = doc.graph.pathToRoot(node).reverse();
      const topGroup = pathFromRoot.find(n => nodesToMove.has(n));
      if (topGroup !== undefined) {
        nodesToReparent.add(topGroup);
      } else {
        nodesToReparent.add(node);
      }
    }

    // If there are no nodes to alter, return early.
    if (nodesToMove.size === 0) {
      return;
    }

    doc = doc.produce('finishArrange', draft => {
      const doc = draft as Doc;
      // Offset the bounds of the nodes to move by the delta.
      for (const node of nodesToMove) {
        const layout = doc.nodeLayout(node);
        layout.center = layout.center.add(delta);
      }

      // Reparent nodes to the new parent group.
      for (const node of nodesToReparent) {
        if (newParent === undefined) {
          // Reparent from group to root
          doc.graph.moveNode(node);
        } else {
          const inEdge = doc.graph.inEdge(node);
          if (inEdge !== undefined) {
            // Reparent to another group
            doc.graph.moveNode(node, { newParent });
          } else {
            // Reparent from root to group
            const newEdge = newIdentifier();
            doc.graph.moveNode(node, { newParent, newEdge });
          }
        }
      }

      // If the reparenting operation would create a cycle in the forest, reject
      // the operation.
      if (!isForest(doc.graph.forest)) {
        throw new Error(
          localeManager.localizedString(StringId.ERROR_CANNOT_MOVE_GROUP_INTO_ITSELF)
        );
      }
    });

    if (!dryRun) {
      doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    }
    const { pan, zoom } = revealBounds(
      expectDefined(doc.elementsBounds(selection)),
      context
    );
    const result = { doc, pan, zoom };
    const needsUndo = !dryRun;
    context.update(result, needsUndo);
    return result;
  } catch (e) {
    if (!dryRun) {
      console.warn(e);
      const message =
        e instanceof Error
          ? e.message
          : localeManager.localizedString(StringId.ERROR_GENERIC);
      void showNotification(message, { variant: SlVariant.WARNING });
      return;
    } else {
      throw e;
    }
  }
}
